/**
 * Phase 13 / Task 13.2a (migration 0233) — "Enter reading" on a Tanks tab row.
 *
 * A dispatcher who gauged a tank, or was told its level, records it here: the level in BBL and
 * when it was read (now by default, in the dispatcher's local time). The Tanks tab then uses it
 * for the fill rate and the last level. The dialog also lists the tank's recent readings, and a
 * wrong one can be voided (it then counts for nothing; nothing is ever deleted).
 *
 * One client key per opening of the form, so a double click or a retried request stores one
 * reading (the API answers the stored row).
 *
 * Tank registry PR 1 (0245): the level can be entered in BBL or in inches (a unit toggle). The
 * API turns inches into barrels with the tank's bbl per inch; when the tank has none yet, the
 * reading is kept with no barrels and the list shows it raw: "8 ft 4 in, size unknown" (13.8,
 * D-23c; a producer's shared reading too).
 */

import * as React from 'react';
import { toast } from 'sonner';
import { ApiError, useApiOpts } from '@lodex-pro/web-shared';
import { formatTankReading } from '@lodex-pro/shared/tank-level-format';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'src/components/ui/dialog';
import { formatTimestamp } from '../../lib/timestamp';
import { useUserTimezone } from '../../lib/use-user-timezone';
import {
  useAddTankReading,
  useTankReadings,
  useVoidTankReading,
  type TankReadingRow,
} from './use-tank-readings';

export interface TankReadingTank {
  readonly tankId: string;
  readonly tankName: string;
  readonly capacityBbl: string | null;
}

/** Date → value for <input type="datetime-local"> in the browser's local time. */
function toLocalInput(d: Date): string {
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${String(d.getFullYear())}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function newClientKey(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `tr-${String(Date.now())}-${Math.random().toString(36).slice(2, 12)}`;
}

/** 13.2b — who entered a shared reading, in plain words. */
export function sharedSourceLabel(source: string): string {
  if (source === 'DISPATCHER_ENTRY') return 'another hauler';
  if (source === 'MQTT') return 'tank sensor';
  return 'the operator';
}

/** Plain words for the API's refusals. */
export function readingErrorMessage(err: unknown, tank: TankReadingTank): string {
  if (err instanceof ApiError) {
    const reason = err.fields[0]?.reason;
    if (reason === 'in_the_future') return 'That time is in the future.';
    if (reason === 'too_old') return 'That time is more than a year ago.';
    if (reason === 'over_capacity') {
      return `That is more than ${tank.tankName} holds (${tank.capacityBbl ?? '?'} BBL).`;
    }
    if (reason === 'inactive') return `${tank.tankName} is inactive.`;
    if (err.status === 404) return `${tank.tankName} is no longer in your catalog.`;
  }
  return 'The reading was not saved. Try again.';
}

export type ReadingUnit = 'BBL' | 'IN';

/**
 * A reading row for display (13.8, D-23c): the shared formatter, as stored. "120 BBL",
 * "120 BBL (4 ft 0 in)", or, with no barrels yet, "4 ft 0 in, size unknown". Never an estimate.
 */
export function tankReadingText(r: {
  readonly levelBbl: number | null;
  /** Older rows may omit it. */
  readonly levelInches?: number | null;
}): string {
  const levelIn = r.levelInches ?? null;
  return formatTankReading({
    levelIn,
    volumeBbl: r.levelBbl,
    needsSize: r.levelBbl === null && levelIn !== null,
  });
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  minHeight: '40px',
  padding: '8px 10px',
  fontSize: '14px',
  backgroundColor: 'var(--color-bg-tertiary)',
  border: '1px solid var(--color-border-secondary)',
  borderRadius: '10px',
  color: 'var(--color-text-primary)',
};

const buttonStyle: React.CSSProperties = {
  minHeight: '36px',
  padding: '0 12px',
  borderRadius: 'var(--radius-pill)',
  border: '1px solid var(--color-border-primary)',
  background: 'var(--color-bg-tertiary)',
  color: 'var(--color-text-primary)',
  cursor: 'pointer',
};

function ReadingItem({
  tankId,
  reading,
  timeZone,
}: {
  tankId: string;
  reading: TankReadingRow;
  timeZone: string;
}): React.JSX.Element {
  const apiOpts = useApiOpts();
  const voidIt = useVoidTankReading(apiOpts, tankId, reading.id);
  const voided = reading.voidedAt !== null;
  return (
    <li
      data-testid="tank-reading"
      style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', padding: '4px 0' }}
    >
      <span style={voided ? { textDecoration: 'line-through', opacity: 0.6 } : undefined}>
        {tankReadingText(reading)} · {formatTimestamp(reading.readAt, timeZone)}
        {reading.enteredByName !== null && <> · {reading.enteredByName}</>}
        {reading.shared === true && <> · {sharedSourceLabel(reading.source)}</>}
      </span>
      {reading.shared === true ? null : voided ? (
        <span style={{ fontSize: '12px' }}>voided</span>
      ) : (
        <button
          type="button"
          style={{ ...buttonStyle, minHeight: '28px' }}
          disabled={voidIt.isPending}
          onClick={(): void => {
            if (!window.confirm('Void this reading? It will no longer count for this tank.')) {
              return;
            }
            voidIt.mutate(
              {},
              {
                onSuccess: () => toast.success('Reading voided'),
                onError: () => toast.error('The reading could not be voided. Try again.'),
              },
            );
          }}
        >
          Void
        </button>
      )}
    </li>
  );
}

export function TankReadingButton({ tank }: { tank: TankReadingTank }): React.JSX.Element {
  const apiOpts = useApiOpts();
  const timeZone = useUserTimezone();
  const [open, setOpen] = React.useState(false);
  const [level, setLevel] = React.useState('');
  const [unit, setUnit] = React.useState<ReadingUnit>('BBL');
  const [readAt, setReadAt] = React.useState('');
  const [clientKey, setClientKey] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  const add = useAddTankReading(apiOpts, tank.tankId);
  const readings = useTankReadings(apiOpts, tank.tankId, open);

  const openForm = (): void => {
    setLevel('');
    setReadAt(toLocalInput(new Date()));
    setClientKey(newClientKey());
    setError(null);
    setOpen(true);
  };

  const levelNumber = level.trim() === '' ? NaN : Number(level);
  const levelValid = Number.isFinite(levelNumber) && levelNumber >= 0;

  const submit = (): void => {
    if (!levelValid) {
      setError(unit === 'BBL' ? 'Enter the level in BBL.' : 'Enter the level in inches.');
      return;
    }
    const when = readAt === '' ? new Date() : new Date(readAt);
    add.mutate(
      {
        ...(unit === 'BBL' ? { levelBbl: levelNumber } : { levelInches: levelNumber }),
        readAt: when.toISOString(),
        clientKey,
      },
      {
        onSuccess: () => {
          toast.success(`Reading saved for ${tank.tankName}`);
          setOpen(false);
        },
        onError: (err) => setError(readingErrorMessage(err, tank)),
      },
    );
  };

  const recent = (readings.data?.readings ?? []).slice(0, 5);

  return (
    <>
      <button
        type="button"
        style={buttonStyle}
        aria-label={`Enter a reading for ${tank.tankName}`}
        onClick={openForm}
      >
        Enter reading
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reading for {tank.tankName}</DialogTitle>
            <DialogDescription>
              The level you gauged or were told. It counts toward this tank&apos;s fill rate.
            </DialogDescription>
          </DialogHeader>
          <div role="radiogroup" aria-label="Unit" style={{ display: 'flex', gap: '6px' }}>
            {(
              [
                ['BBL', 'BBL'],
                ['IN', 'Inches'],
              ] as const
            ).map(([value, label]) => (
              <button
                key={value}
                type="button"
                role="radio"
                aria-checked={unit === value}
                onClick={(): void => setUnit(value)}
                style={{
                  ...buttonStyle,
                  minHeight: '28px',
                  background:
                    unit === value ? 'var(--color-accent-blue)' : 'var(--color-bg-tertiary)',
                  color: unit === value ? 'var(--color-on-accent)' : 'var(--color-text-primary)',
                }}
              >
                {label}
              </button>
            ))}
          </div>
          <label style={{ display: 'block', fontSize: '13px' }}>
            {unit === 'BBL' ? 'Level (BBL)' : 'Level (inches)'}
            <input
              type="number"
              inputMode="decimal"
              min={0}
              step="0.01"
              value={level}
              onChange={(e): void => setLevel(e.target.value)}
              style={inputStyle}
              aria-label={unit === 'BBL' ? 'Level (BBL)' : 'Level (inches)'}
            />
          </label>
          <label style={{ display: 'block', fontSize: '13px' }}>
            Read at
            <input
              type="datetime-local"
              value={readAt}
              onChange={(e): void => setReadAt(e.target.value)}
              style={inputStyle}
              aria-label="Read at"
            />
          </label>
          {error !== null && (
            <p role="alert" style={{ color: 'var(--color-danger)', margin: 0 }}>
              {error}
            </p>
          )}
          {recent.length > 0 && (
            <div>
              <div style={{ fontSize: '12px', color: 'var(--color-text-tertiary)' }}>
                Recent readings
              </div>
              <ul style={{ listStyle: 'none', margin: 0, padding: 0, fontSize: '13px' }}>
                {recent.map((r) => (
                  <ReadingItem key={r.id} tankId={tank.tankId} reading={r} timeZone={timeZone} />
                ))}
              </ul>
            </div>
          )}
          <DialogFooter>
            <button type="button" style={buttonStyle} onClick={(): void => setOpen(false)}>
              Cancel
            </button>
            <button
              type="button"
              style={{
                ...buttonStyle,
                background: 'var(--color-accent-blue)',
                color: 'var(--color-on-accent)',
              }}
              disabled={add.isPending}
              onClick={submit}
            >
              {add.isPending ? 'Saving…' : 'Save reading'}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
